import { useCallback, useEffect, useMemo, useState } from "react";

import { useAppSelector } from "../../../redux/store";
import { apiGet, apiPost } from "../../../services/api/apiClient";
import {
  Button,
  ErrorState,
  Field,
  Loading,
  PageHeader,
  Section,
  Select,
  Tag,
  Textarea,
  Toolbar,
} from "../../../ui";

type SectionMap = {
  key: string;
  label: string;
  status: "found" | "not_found" | "override";
  folderIds?: string[];
  resolvedPath?: string;
  overrideId?: string;
};

type AdminMap = {
  projectId: string;
  sections: SectionMap[];
  unmapped?: Array<{ dropboxId: string; name: string; path: string }>;
  resolvedAt: string;
};

type CustomerOption = { id: string; name: string };

const statusTag = (status: SectionMap["status"]) => {
  if (status === "found") return <Tag tone="green">Found</Tag>;
  if (status === "override") return <Tag tone="brand">Override</Tag>;
  return <Tag tone="amber">Not found</Tag>;
};

const when = (value: string) => {
  if (!value) return "never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "never" : date.toLocaleString();
};

export default function DocumentLibraryAdmin() {
  const source = useAppSelector((state) => state.source.source);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [projectId, setProjectId] = useState("");
  const [map, setMap] = useState<AdminMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [folders, setFolders] = useState<Array<{ dropboxId: string; name: string; path: string }>>([]);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [savingOverride, setSavingOverride] = useState<string | null>(null);
  const [rules, setRules] = useState<any[]>([]);
  const [rulesText, setRulesText] = useState("");
  const [rulesSaving, setRulesSaving] = useState(false);
  const [rulesError, setRulesError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiGet({
      endpoint: "/get-customers-by-filters",
      params: source ? { source } : undefined,
      withAuth: true,
    })
      .then((data: any) => {
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : data?.data || [];
        const options = rows
          .map((row: any) => ({
            id: String(row.id || row._id || ""),
            name: row.name || row.companyName || "Unnamed project",
          }))
          .filter((row: CustomerOption) => row.id);
        setCustomers(options);
        if (options[0]) setProjectId(options[0].id);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || "Could not load customers.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  const loadMap = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const data: AdminMap = await apiGet({
        endpoint: "/admin/document-library/map",
        params: { projectId },
        withAuth: true,
      });
      setMap(data);
      const nextOverrides: Record<string, string> = {};
      (data.sections || []).forEach((section) => {
        nextOverrides[section.key] = section.overrideId || "";
      });
      setOverrides(nextOverrides);
    } catch (err: any) {
      setError(err?.message || "Could not load the document map.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadMap();
  }, [loadMap]);

  useEffect(() => {
    if (!projectId) {
      setFolders([]);
      return;
    }
    let cancelled = false;
    apiGet({
      endpoint: "/admin/document-library/folders",
      params: { projectId },
      withAuth: true,
    })
      .then((data: any) => {
        if (!cancelled) setFolders(data?.folders || []);
      })
      .catch(() => {
        if (!cancelled) setFolders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    apiGet({
      endpoint: "/admin/document-library/rules",
      withAuth: true,
    })
      .then((data: any) => {
        const rows = data?.rules || [];
        setRules(rows);
        setRulesText(JSON.stringify(rows, null, 2));
      })
      .catch((err: any) => setRulesError(err?.message || "Could not load rules."));
  }, []);

  const saveRules = async () => {
    setRulesSaving(true);
    setRulesError(null);
    try {
      const parsed = JSON.parse(rulesText);
      const data: any = await apiPost({
        endpoint: "/admin/document-library/rules/save",
        data: parsed,
        withAuth: true,
      });
      setRules(parsed);
      setRulesError(data?.message || "Rules saved. Refresh projects to apply them.");
    } catch (err: any) {
      setRulesError(err?.message || "Could not save rules. Check the JSON.");
    } finally {
      setRulesSaving(false);
    }
  };

  const refresh = async (all = false) => {
    setRefreshing(true);
    setError(null);
    try {
      await apiPost({
        endpoint: "/admin/document-library/refresh",
        params: all ? undefined : { projectId },
        withAuth: true,
      });
      await loadMap();
    } catch (err: any) {
      setError(err?.message || "Refresh failed.");
    } finally {
      setRefreshing(false);
    }
  };

  const saveOverride = async (sectionKey: string, value?: string) => {
    setSavingOverride(sectionKey);
    setError(null);
    try {
      await apiPost({
        endpoint: "/admin/document-library/override",
        data: {
          projectId,
          sectionKey,
          folderId: (value ?? overrides[sectionKey] ?? "").trim(),
        },
        withAuth: true,
      });
      await loadMap();
    } catch (err: any) {
      setError(err?.message || "Could not save the override.");
    } finally {
      setSavingOverride(null);
    }
  };

  const selected = useMemo(
    () => customers.find((customer) => customer.id === projectId),
    [customers, projectId],
  );

  return (
    <>
      <PageHeader
        eyebrow="Documents"
        title="Document Library"
        summary="How each client section maps to the PUBLIC folder, and where it needs an override."
      />

      <Toolbar
        title="Project"
        actions={
          <>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => refresh(false)}
              disabled={refreshing || !projectId}
            >
              {refreshing ? "Refreshing…" : "Refresh project"}
            </Button>
            <Button size="sm" onClick={() => refresh(true)} disabled={refreshing}>
              {refreshing ? "Refreshing…" : "Refresh all"}
            </Button>
          </>
        }
      >
        <Select
          value={projectId}
          onChange={(event) => setProjectId(event.target.value)}
          aria-label="Project"
          style={{ minWidth: 280 }}
        >
          {customers.map((customer) => (
            <option key={customer.id} value={customer.id}>
              {customer.name}
            </option>
          ))}
        </Select>
      </Toolbar>

      {error && <ErrorState what="the folder map" message={error} onRetry={loadMap} />}
      {loading && <Loading>Loading folder map…</Loading>}

      {!loading && map && (
        <>
          <Section
            title="Section map"
            hint={`${selected?.name || "Project"} · resolved ${when(map.resolvedAt)}`}
          >
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th>Section</th>
                    <th>Status</th>
                    <th>Resolved folder</th>
                    <th>Override</th>
                  </tr>
                </thead>
                <tbody>
                  {map.sections.map((section) => (
                    <tr key={section.key}>
                      <td>{section.label}</td>
                      <td>{statusTag(section.status)}</td>
                      <td>
                        {section.resolvedPath ? (
                          <code style={{ fontSize: 12 }}>{section.resolvedPath}</code>
                        ) : (
                          <span className="ui-muted">Not found</span>
                        )}
                      </td>
                      <td>
                        <div className="ui-row" style={{ gap: 6 }}>
                          <Select
                            value={overrides[section.key] || ""}
                            onChange={(event) =>
                              setOverrides((current) => ({
                                ...current,
                                [section.key]: event.target.value,
                              }))
                            }
                            aria-label={`Override folder for ${section.label}`}
                            style={{ minWidth: 300, maxWidth: 420, fontSize: 12 }}
                          >
                            <option value="">Use the folder the rules find</option>
                            {section.overrideId &&
                              !folders.some((folder) => folder.dropboxId === section.overrideId) && (
                                <option value={section.overrideId}>
                                  Current override ({section.overrideId})
                                </option>
                              )}
                            {folders.map((folder) => (
                              <option key={folder.dropboxId} value={folder.dropboxId}>
                                {folder.path}
                              </option>
                            ))}
                          </Select>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => saveOverride(section.key)}
                            disabled={savingOverride === section.key}
                          >
                            {savingOverride === section.key ? "Saving…" : "Save"}
                          </Button>
                          {section.overrideId && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setOverrides((current) => ({ ...current, [section.key]: "" }));
                                saveOverride(section.key, "");
                              }}
                            >
                              Clear
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section
            title="Not shown to client"
            hint="Folders that match no section. They stay out of the client library until mapped."
          >
            {map.unmapped?.length ? (
              <div className="ui-table-wrap">
                <table className="ui-table">
                  <thead>
                    <tr>
                      <th>Folder</th>
                      <th>Path</th>
                    </tr>
                  </thead>
                  <tbody>
                    {map.unmapped.map((folder) => (
                      <tr key={folder.dropboxId || folder.path}>
                        <td>{folder.name}</td>
                        <td>
                          <code style={{ fontSize: 12 }}>{folder.path}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="ui-small ui-muted">Every top-level folder is covered by a section.</p>
            )}
          </Section>
        </>
      )}

      <Section
        title="Section rules"
        hint="One table for every client. Patterns are relative folder paths; leading numbers and case are ignored."
        right={
          <Button size="sm" variant="secondary" onClick={saveRules} disabled={rulesSaving}>
            {rulesSaving ? "Saving…" : "Save rules"}
          </Button>
        }
      >
        <div className="ui-table-wrap ui-mb-16">
          <table className="ui-table">
            <thead>
              <tr>
                <th>Section</th>
                <th>Mode</th>
                <th>Patterns</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.key}>
                  <td>
                    {rule.label} <span className="ui-caption">({rule.key})</span>
                  </td>
                  <td>{rule.mode}</td>
                  <td>
                    {(rule.patterns || []).map((pattern: string[], index: number) => (
                      <code key={index} style={{ display: "block", fontSize: 11 }}>
                        {pattern.join(" / ")}
                      </code>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <details>
          <summary className="ui-caption" style={{ cursor: "pointer" }}>
            Edit rules JSON
          </summary>
          <div className="ui-mt-8">
            <Textarea
              value={rulesText}
              onChange={(event) => setRulesText(event.target.value)}
              rows={18}
              spellCheck={false}
              style={{ fontFamily: "monospace", fontSize: 12 }}
            />
          </div>
        </details>
        {rulesError && <p className="ui-caption ui-mt-8">{rulesError}</p>}
      </Section>

      {!loading && !map && !error && (
        <Field label="Project">
          <p className="ui-small ui-muted">Choose a project to inspect its map.</p>
        </Field>
      )}
    </>
  );
}
